import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Box,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle,
  Error,
  TrendingUp,
  School,
  Timer
} from '@mui/icons-material';
import axios from 'axios';

function ProgressDashboard({ session, onEndSession }) {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProgress();
  }, [session.sessionId]);

  const fetchProgress = async () => {
    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');

      // Fetch detailed progress
      const progressResponse = await axios.get(
        `${baseUrl}/api/progress/${session.sessionId}`
      );

      // Fetch session summary
      const summaryResponse = await axios.get(
        `${baseUrl}/api/session/${session.sessionId}/summary`
      );

      if (progressResponse.data.success && summaryResponse.data.success) {
        setProgress(progressResponse.data.progress);
        setSummary(summaryResponse.data.summary);
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const backToQuestions = () => {
    navigate('/question');
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 0.8) return 'success';
    if (accuracy >= 0.6) return 'warning';
    return 'error';
  };

  const formatTime = (milliseconds) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" style={{ marginTop: '2rem' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (!progress || !summary) {
    return (
      <Container maxWidth="md" style={{ marginTop: '2rem' }}>
        <Typography variant="h6" color="error">
          Unable to load progress data.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" style={{ marginTop: '2rem' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="2rem">
        <Typography variant="h4" component="h1">
          Learning Progress
        </Typography>
        <Box display="flex" gap="1rem">
          <Button variant="outlined" onClick={backToQuestions}>
            Back to Questions
          </Button>
          <Button variant="contained" color="secondary" onClick={onEndSession}>
            End Session
          </Button>
        </Box>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} marginBottom="3rem">
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" marginBottom="1rem">
                <School color="primary" style={{ marginRight: '0.5rem' }} />
                <Typography variant="h6">Current Chapter</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {progress.currentChapter}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Numbers and Operations
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" marginBottom="1rem">
                <CheckCircle color="success" style={{ marginRight: '0.5rem' }} />
                <Typography variant="h6">Accuracy</Typography>
              </Box>
              <Typography variant="h4" color="success">
                {Math.round(summary.accuracy * 100)}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {summary.correctAnswers} / {summary.totalQuestions} correct
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" marginBottom="1rem">
                <Timer color="info" style={{ marginRight: '0.5rem' }} />
                <Typography variant="h6">Time Spent</Typography>
              </Box>
              <Typography variant="h4" color="info">
                {formatTime(summary.timeSpent)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Learning session duration
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" marginBottom="1rem">
                <TrendingUp color="warning" style={{ marginRight: '0.5rem' }} />
                <Typography variant="h6">Avg Response</Typography>
              </Box>
              <Typography variant="h4" color="warning">
                {Math.round(summary.averageResponseTime / 1000)}s
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Per question
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Chapter Progress */}
      <Card elevation={3} style={{ marginBottom: '2rem' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Chapter Progress
          </Typography>
          <Box marginBottom="1rem">
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Chapter {progress.currentChapter} Mastery
            </Typography>
            <LinearProgress
              variant="determinate"
              value={summary.accuracy * 100}
              style={{ height: 10, borderRadius: 5 }}
              color={getAccuracyColor(summary.accuracy)}
            />
          </Box>
          <Typography variant="body2" color="textSecondary">
            Target: 75% to advance to next chapter
          </Typography>
        </CardContent>
      </Card>

      {/* Topic Performance */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Topic Performance
              </Typography>
              <List>
                {Object.entries(progress.topicPerformance).map(([topic, performance]) => (
                  <ListItem key={topic}>
                    <ListItemIcon>
                      {performance.accuracy >= 0.8 ? (
                        <CheckCircle color="success" />
                      ) : (
                        <Error color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={topic.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      secondary={`${Math.round(performance.accuracy * 100)}% accuracy (${performance.correct}/${performance.total} questions)`}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Learning Insights
              </Typography>

              {/* Strengths */}
              {summary.strengths && summary.strengths.length > 0 && (
                <Box marginBottom="1.5rem">
                  <Typography variant="subtitle2" color="success" gutterBottom>
                    Strengths:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap="0.5rem">
                    {summary.strengths.map((strength, index) => (
                      <Chip
                        key={index}
                        label={strength}
                        color="success"
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Areas to Improve */}
              {summary.strugglingTopics && summary.strugglingTopics.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="warning" gutterBottom>
                    Areas to Improve:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap="0.5rem">
                    {summary.strugglingTopics.map((topic, index) => (
                      <Chip
                        key={index}
                        label={topic}
                        color="warning"
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {summary.strengths && summary.strengths.length === 0 &&
                summary.strugglingTopics && summary.strugglingTopics.length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    Keep practicing to build your learning profile!
                  </Typography>
                )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}

export default ProgressDashboard;